import * as React from "react";
import styled, { useTheme } from "styled-components";

import { BaseTile } from "./Tile";
import { EmailOldIcon } from "../../../ui/icons/Email";

import { useProgressiveImage } from "../../../utils/hooks/useProgressiveImage";

interface IBrokerItem {
  name?: string | null;
  image?: string | null | undefined;
  path?: string | null;
  phone?: string | null;
  title?: string | null;
  office?: string | null;
  urltag?: string | null;
}

export const BrokerItem: React.FC<IBrokerItem> = React.memo(
  ({ name, image, path, phone, title, office, urltag }) => {
    const brokerPath = `https://privatmegleren.no/megler/${path}`;
    return (
      <StyledBrokerItem>
        <BrokerImage url={image || ""} />
        <StyledBrokerInfo>
          <a rel="noopener" href={brokerPath} className="name">
            {name}
          </a>
          <span className="title">{title}</span>
          <a
            href={`https://privatmegleren.no/${urltag}`}
            target="_blank"
            rel="noopener"
            className="office"
          >
            PrivatMegleren {office}
          </a>
          <div className="contact">
            <a href={`tel:${phone?.replace(/ /g, "")}`}>{phone}</a>
            <span
              onClick={() => window.open(`${brokerPath}/kontakt`, "_blank")}
            >
              <EmailOldIcon />
            </span>
          </div>
          <AboutBroker href={brokerPath}>Om megler</AboutBroker>
        </StyledBrokerInfo>
      </StyledBrokerItem>
    );
  }
);

const BrokerImage: React.FC<{ url?: string }> = React.memo(({ url }) => {
  const ref = React.useRef(null);
  const { loaded } = useProgressiveImage({
    url: url,
  });

  return (
    <StyledBrokerImage ref={ref}>
      <img
        alt="meglerbilde"
        data-url={url}
        className={loaded ? "show" : ""}
        src={loaded ? url : ""}
      />
    </StyledBrokerImage>
  );
});

const StyledBrokerItem = styled(BaseTile)`
  display: grid;
  grid-template-columns: 150px 1fr;
  grid-column-gap: 20px;
  align-items: center;
  height: 200px;
  background: ${({ theme }) => theme.tile.broker.background};
  background-position: 0 -13px;

  @media all and (max-width: 960px) {
    grid-template-columns: 1fr;
    grid-row-gap: 20px;
    padding: 40px 0;
    height: auto;
  }
`;

const StyledBrokerImage = styled.div`
  position: relative;
  justify-self: center;
  width: 150px;
  height: 150px;
  border-radius: 150px;
  overflow: hidden;

  @media all and (max-width: 1600px) {
    width: 105px;
    height: 105px;
  }

  img {
    transition: opacity 0.2s ease-in-out;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    object-fit: cover;
    &.show {
      opacity: 1;
    }
  }
  @media all and (max-width: 960px) {
    justify-self: center;
  }
`;

const StyledBrokerInfo = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;

  .title {
    color: ${({ theme }) => theme.tile.broker.title};
  }
  .office {
    color: ${({ theme }) => theme.tile.broker.link};
  }

  a {
    color: ${({ theme }) => theme?.tile?.broker?.link};
    text-decoration: none;
    margin: 0 0 0.3em;

    &.name {
      font-size: 1.2em;
      @media all and (max-width: 960px) {
        font-family: "DomaineDisp", sans-serif;
        font-size: 1.5em;
      }
    }
  }
  .contact {
    display: grid;
    grid-template-columns: max-content 30px;
    grid-column-gap: 20px;

    svg {
      rect {
        fill: ${({ theme }) => theme?.tile?.broker.mail.fill};
      }
      path {
        stroke: ${({ theme }) => theme?.tile?.broker.mail.stroke};
      }
    }
  }

  @media all and (max-width: 960px) {
    text-align: center;
    padding: 0 2em;
    .contact {
      justify-content: center;
    }
  }
`;

const AboutBroker = styled.a`
  color: white !important;
  padding: 0.3em 0;
  border-bottom: 2px solid rgb(255, 90, 90);
  width: max-content;

  @media all and (max-width: 960px) {
    margin: 0 auto !important;
  }
`;
